import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useLocalSearchParams } from 'expo-router';
import { ScrollView } from 'react-native';

import { Header } from '@/components/header';
import { AboutSection } from '@/components/profile/AboutSection';
import { AnonymousProfileView } from '@/components/profile/AnonymousProfileView';
import { ProfessionalSection } from '@/components/profile/ProfessionalSection';
import { ProfileChips } from '@/components/profile/ProfileChips';
import { Spinner } from '@/components/ui/spinner';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ROUTES } from '@/constants/routes';
import { useColors } from '@/hooks/useColors';
import { useExpertProfileData } from '@/hooks/useExpertProfileData';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { segmentClient } from '@/util/analytics';
import { shownTrialCount } from '@/util/profileTrialCounts';

type PublicUserProfileProps = {
  /**
   * The expert to show. Defaults to the `id` route param; a route that resolves
   * the expert some other way, such as a personal link's slug, passes it here.
   */
  userId?: number;
};

const PublicUserProfile = ({ userId }: PublicUserProfileProps = {}) => {
  const params = useLocalSearchParams<{ id: string }>();

  const id = userId?.toString() ?? params.id;

  const { colors } = useColors();

  const institutionalTrialsEnabled = useFeatureFlag(FEATURE_FLAGS.INSTITUTIONAL_TRIALS);

  const {
    userData,
    isLoading,
    isAnonymous,
    nameWithoutDoctor,
    cancerFocusAreas,
    subspecialtyItems,
    responseTimeLabel,
    bioSections,
  } = useExpertProfileData(id, { mode: 'public-only' });

  const numericId = userData?.id;

  const showChips = !!numericId;

  const aboutSection = bioSections.find((section) => section.title === 'About Me');

  const content =
    userData && isAnonymous ? (
      <AnonymousProfileView userId={userData.id || ''} expertId={id || ''} />
    ) : (
      <ScrollView
        className="w-full flex-1"
        contentInsetAdjustmentBehavior="automatic"
        contentContainerStyle={{ paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>
        <ProfessionalSection
          userData={userData}
          isOwnProfile={false}
          nameWithoutDoctor={nameWithoutDoctor}
          responseTimeLabel={responseTimeLabel}
          cancerFocusAreas={cancerFocusAreas}
          subspecialtyItems={subspecialtyItems}
          onEditSummary={() => {}}
          onSendMessage={() => {
            segmentClient.track(ANALYTICS_EVENTS.BIO_MESSAGE_SELECTED, {
              expert_id: id,
            });
          }}
          messageUrl={`/directory/inquiry/${userData?.id?.toString() || ''}`}
          chipSlot={
            showChips && numericId ? (
              <ProfileChips
                expertId={numericId}
                publicationsCount={userData?.publicationsCount ?? 0}
                clinicalTrialsCount={shownTrialCount(userData, institutionalTrialsEnabled)}
              />
            ) : null
          }
        />
        {aboutSection ? (
          <AboutSection section={aboutSection} isFirstSection={false} isOwnProfile={false} />
        ) : null}
      </ScrollView>
    );

  return (
    <VStack className="w-full flex-1">
      <Header
        isBackButton
        backRoute={ROUTES.DIRECTORY}
        statusBarBackgroundColor={colors.background[50]}
        headerStyle={{ backgroundColor: colors.background[50] }}
        contentScrollsUnderHeader
        title={isWeb ? undefined : nameWithoutDoctor}
        headerTitle={isWeb ? '' : undefined}
        className="bg-background-50"
      />

      <VStack className="flex-1">
        {isLoading ? (
          <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0" />
        ) : (
          content
        )}
      </VStack>
    </VStack>
  );
};

export default PublicUserProfile;
